import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ProviderSurveyResponseService, type Email } from '../../../../../js/api';
import { Button } from '../../../components/button';
import { Field, Fieldset, Label } from '../../../components/fieldset';
import { Input } from '../../../components/input';
import { Section } from '../../../components/section';
import { SectionHeader } from '../../../components/SectionHeader';
import { Text } from '../../../components/text';

// TODO: derive this from the correct model
interface SurveyData {
  id: string;
  current_status: {
    code: string;
    title: string;
    category: string;
    category_title: string;
    category_kind: string;
  };
  provider_survey: {
    provider: {
      prefix_name: string;
      preferred_name: string;
      last_name: string;
    };
  };
}

interface ProviderSurveyEmailLookupProps {
  onFoundSurveyId?: (surveyId: string) => void;
}

export const ProviderSurveyEmailLookup: React.FC<ProviderSurveyEmailLookupProps> = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [surveys, setSurveys] = useState<SurveyData[]>([]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setMessage(null);
    setSurveys([]);
    setIsSubmitting(true);
    try {
      const payload: Email = { email };
      console.log('Looking up outstanding surveys for email:', email);

      const result = await ProviderSurveyResponseService.providerSurveyResponsesOutstanding({
        requestBody: payload,
      });

      const surveysData = Array.isArray(result) ? result : result?.results || [];

      if (surveysData.length > 0) {
        setSurveys(surveysData as SurveyData[]);
        setMessage(`Found ${surveysData.length} outstanding survey request(s).`);
      } else {
        setMessage('No outstanding survey requests found for this email.');
      }
    } catch (err) {
      console.error('Error fetching surveys:', err);
      setError('There was an issue looking up your surveys. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBeginSurvey = (surveyId: string) => {
    navigate(`/endorsement/provider-survey/${surveyId}`);
  };

  return (
    // TODO DARK MODE STYLES: this needs to be a global component
    <main className="tw:max-w-2xl tw:mx-auto tw:px-4 tw:pt-6 tw:pb-10 tw:dark:bg-zinc-700">
      <Section>
        <SectionHeader headerText="Find Your Survey Request" />
        <Text>
          Enter the email address where you received the survey request. We&apos;ll look up
          outstanding requests so you can complete the survey.
        </Text>
        <form onSubmit={handleSubmit}>
          <Fieldset>
            <Field>
              <Label>Email address</Label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </Field>
          </Fieldset>
          {error && <Text className="tw:!text-stoplight-danger tw:!mt-3">{error}</Text>}
          {message && <Text className="tw:!mt-3">{message}</Text>}
          <div className="tw:flex tw:justify-end tw:mt-6">
            <Button color="brandgreen0" type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Searching…' : 'Find survey(s)'}
            </Button>
          </div>
        </form>

        {surveys.length > 0 && (
          <div className="tw:mt-6">
            <SectionHeader
              headerText="Outstanding Survey Requests"
              className="tw:!font-medium tw:mb-4"
            />
            <div className="tw:space-y-4">
              {surveys.map((survey) => (
                <div
                  key={survey.id}
                  // TODO DARK MODE STYLES: these need to be defined as global section-with-section styles
                  className="tw:border tw:border-zinc-200 tw:rounded-lg tw:p-4 tw:bg-zinc-50 tw:dark:bg-white/5 tw:dark:border-white/10"
                >
                  <div className="tw:flex tw:justify-between tw:items-center">
                    <div className="tw:flex-1">
                      <div className="tw:flex tw:items-center tw:gap-2 tw:mb-2">
                        {/* <span className="tw:font-medium">Survey ID: {survey.id}</span> */}
                        <span className="tw:font-medium tw:dark:text-white">
                          {survey.provider_survey.provider.prefix_name}{' '}
                          {survey.provider_survey.provider.preferred_name}{' '}
                          {survey.provider_survey.provider.last_name}
                        </span>
                        {/* TODO: do we need a component for this status pill? only outstanding statuses will show here */}
                        <span className="tw:px-2 tw:py-1 tw:bg-blue-100 tw:text-blue-800 tw:text-xs tw:rounded-full">
                          {survey.current_status.title}
                        </span>
                      </div>
                    </div>
                    <div className="tw:ml-4">
                      <Button color="brandgreen0" onClick={() => handleBeginSurvey(survey.id)}>
                        {survey.current_status.category_kind === 'I' ? 'Begin' : 'Resume'}
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </Section>
    </main>
  );
};

export default ProviderSurveyEmailLookup;
